import type { Follow } from '@flobi/collab-client/react';
import { defaultLayerAnimation, type NodeRecord, type Paint } from '@flobi/artwork-core';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { ASSETS_WIDTH, Library } from '../editor/content/Library';
import { AnimateBar } from '../editor/shell/AnimateBar';
import { DocumentSettings } from '../editor/shell/PageInspector';
import { QuickBar } from '../editor/shell/QuickBar';
import { SidePanel, PANEL_WIDTH } from '../editor/shell/SidePanel';
import { Toolbar } from '../editor/shell/Toolbar';
import { ToolStrip } from '../editor/shell/ToolStrip';
import type { DrawWith } from '../editor/shell/ToolOptions';
import type { Rail, RailButton } from '../editor/tools/types';
import type { AnimationTarget } from '../editor/useEditorCanvas';
import { colorToHex } from '../theme';
import { buildLabDocument, drawRail, labActions } from './labDocument';

/** Nobody to follow: the lab has no collaborators. */
const NOBODY: Follow = { followingId: null, target: null, isFollowing: () => false, follow: () => {}, stop: () => {}, toggle: () => {} };

/**
 * The editor's chrome, as a picture: the real toolbar, dock, tool strip, quick bar, side
 * panel and library around a stand-in canvas with a real camera, in whichever state the
 * address asks for. No backend and nothing to sign in to, so it can be opened — and
 * screenshotted in a headless browser — without a file, an account or a server.
 *
 *   /lab/shell?state=idle|selected|several|text|artboard|more|tool|points|library|animate|settings
 *              &zoom=1.8&x=540&y=300   (the document point at the middle of the view)
 *              &pick=card|sun|title    (what is chosen)
 *              &theme=dark|light
 *
 * The camera is what the quick bar's placement is about: zoom in and pan with `zoom`, `x`
 * and `y` to put the artboard's edges on screen or off it.
 */

const TOP = 60;
const DOCK = 60;
const STRIP = 52;
const noop = () => {};
const css = (paint: Paint | undefined) => (paint && paint.type === 'solid' ? colorToHex(paint.color) : 'transparent');
const DRAW_WITH: DrawWith = { fill: '#5f72a3', fillOn: true, stroke: '#111111', weight: 0, onFill: noop, onFillNone: noop, onStroke: noop, onStrokeNone: noop, onWeight: noop };

const button = (id: string, icon: RailButton['icon'], title: string, extra: Partial<RailButton> = {}): RailButton => ({ id, icon, title, run: noop, ...extra });
const POINTS_RAIL: Rail = {
  top: {
    kind: 'buttons',
    items: [
      button('object', 'select-object', 'Object — select whole shapes', { pressed: false }),
      button('point', 'select-point', 'Direct — select the points inside a shape', { pressed: true }),
      button('area', 'select-area', 'Marquee — drag a box round points', { pressed: false }),
      button('lasso', 'select-lasso', 'Lasso — draw a loop round points', { pressed: false }),
    ],
  },
  bottom: {
    kind: 'buttons',
    items: [
      button('grow', 'plus', 'Take in the anchors either side — ⌘ for the whole path'),
      button('shrink', 'minus', 'Drop the ends of each run'),
      button('mirror', 'mirror', 'Choose across the axis too', { pressed: false }),
      button('invert', 'invert', 'Choose the rest of the path instead'),
    ],
  },
};

export function ShellLab() {
  const params = new URLSearchParams(window.location.search);
  const state = params.get('state') ?? 'idle';
  const [headerHeight, setHeaderHeight] = useState(TOP);
  const theme = params.get('theme');
  useEffect(() => {
    if (theme) document.documentElement.dataset.theme = theme;
  }, [theme]);
  const [, resized] = useState(0);
  useEffect(() => {
    const on = () => resized((n) => n + 1);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  const doc = useMemo(() => buildLabDocument(), []);
  const [version, setVersion] = useState(0);
  useEffect(() => doc.scene.subscribe(() => setVersion((v) => v + 1)), [doc]);
  void version;
  const actions = useMemo(() => labActions(doc), [doc]);

  const tool = state === 'tool' ? 'draw' : 'select';
  // `pick` chooses which thing is chosen in the single-selection states.
  const pick = params.get('pick') === 'sun' ? doc.sun : params.get('pick') === 'title' ? doc.title : doc.card;
  const chosen = state === 'several' ? [doc.card, doc.title] : state === 'text' ? [doc.title] : state === 'artboard' ? [doc.board] : ['selected', 'more', 'points'].includes(state) ? [pick] : [];
  const nodes = chosen.map((id) => doc.scene.get(id)).filter((node): node is NodeRecord => !!node);
  const panel = state === 'more' ? 'format' : state === 'animate' ? 'animate' : null;
  const strip: Rail | null = state === 'tool' ? drawRail('pencil', noop) : state === 'points' ? POINTS_RAIL : null;
  const right = panel === 'format' ? PANEL_WIDTH + 16 : 0;
  const library = state === 'library';

  // The camera: fitted to the page in the room the chrome leaves, unless the address says.
  const board = doc.scene.get(doc.board) as NodeRecord & { width: number; height: number; fills?: Paint[] };
  // As the editor fits: the quick bar's band kept free above the page.
  const room = { left: 0, top: headerHeight + 72, right: window.innerWidth - right, bottom: window.innerHeight - DOCK };
  const fitted = Math.min((room.right - room.left - 96) / board.width, (room.bottom - room.top - 96) / board.height);
  const zoom = Number(params.get('zoom') ?? fitted);
  const centre = { x: Number(params.get('x') ?? board.width / 2), y: Number(params.get('y') ?? board.height / 2) };
  const origin = { x: (room.left + room.right) / 2 - centre.x * zoom, y: (room.top + room.bottom) / 2 - centre.y * zoom };
  const onScreen = (x: number, y: number, width: number, height: number) => ({ left: origin.x + x * zoom, top: origin.y + y * zoom, right: origin.x + (x + width) * zoom, bottom: origin.y + (y + height) * zoom });

  const boxOf = (node: NodeRecord & { width?: number; height?: number }) => onScreen(node.transform[4], node.transform[5], node.width ?? 0, node.height ?? 0);
  const union = nodes.length
    ? nodes.map(boxOf).reduce((a, b) => ({ left: Math.min(a.left, b.left), top: Math.min(a.top, b.top), right: Math.max(a.right, b.right), bottom: Math.max(a.bottom, b.bottom) }))
    : null;
  const boardBox = onScreen(0, 0, board.width, board.height);
  const kids = [doc.card, doc.sun, doc.title].map((id) => doc.scene.get(id)).filter((node): node is NodeRecord => !!node);

  return (
    <div className="editor-workspace fixed inset-0 overflow-hidden bg-canvas text-label" style={{ '--workspace-top': `${headerHeight}px`, '--workspace-bottom': `${DOCK}px` } as CSSProperties}>
      {/* A stand-in canvas: the page and what is on it, through the camera. */}
      <div className="absolute text-[12px] text-label-2" style={{ left: boardBox.left, top: boardBox.top - 20 }}>
        {board.name}
      </div>
      <div className="absolute overflow-hidden" style={{ left: boardBox.left, top: boardBox.top, width: board.width * zoom, height: board.height * zoom, background: css(board.fills?.[0]), boxShadow: '0 2px 16px rgb(0 0 0 / 0.28)' }}>
        {kids.map((node) => {
          const n = node as NodeRecord & { width: number; height: number; radii?: number[]; fills?: Paint[]; runs?: { text: string }[]; style?: { fontSize?: number; fontWeight?: number; fills?: Paint[] } };
          const at = { left: n.transform[4] * zoom, top: n.transform[5] * zoom };
          if (n.type === 'text') {
            return (
              <div key={n.id} className="absolute whitespace-nowrap" style={{ ...at, fontSize: (n.style?.fontSize ?? 16) * zoom, fontWeight: n.style?.fontWeight ?? 400, color: css(n.style?.fills?.[0]), lineHeight: 1.1 }}>
                {n.runs?.map((run) => run.text).join('')}
              </div>
            );
          }
          return <div key={n.id} className="absolute" style={{ ...at, width: n.width * zoom, height: n.height * zoom, background: css(n.fills?.[0]), borderRadius: n.type === 'ellipse' ? '50%' : (n.radii?.[0] ?? 0) * zoom }} />;
        })}
      </div>
      {nodes.map((node) => {
        const box = boxOf(node);
        return <div key={node.id} className="pointer-events-none absolute" style={{ left: box.left, top: box.top, width: box.right - box.left, height: box.bottom - box.top, outline: '1.5px solid #3d82ff' }} />;
      })}

      <Toolbar
        onHeight={setHeaderHeight}
        name="Summer Poster"
        onRename={noop}
        documentMenu={[]}
        settings={state === 'settings' ? <DocumentSettings units="px" resolution={72} grid={{ step: 72, divisions: 8 }} colorMode="rgb" press={null} onUnits={noop} onResolution={noop} onGrid={noop} onColorMode={noop} /> : null}
        onSettingsClose={noop}
        exportOptions={{ scope: 'board', scopes: [{ id: 'board', label: 'Current', detail: 'Summer Poster' }], onScopeChange: noop, count: 1, resolution: 72, slices: 0, size: { width: 1080, height: 1350 }, onExport: noop }}
        zoomMenu={[]}
        zoom={zoom}
        room={null}
        follow={NOBODY}
        readOnly={false}
        tool={tool}
        shapeIcon="square"
        onTool={noop}
        hiddenTools={new Set()}
        onFiles={noop}
        adding={library}
        onAdd={noop}
        onUndo={noop}
        onRedo={noop}
        canUndo
        canRedo={false}
        animating={panel === 'animate'}
        onAnimate={noop}
        addonInHand={null}
        onAddons={noop}
        onZoomBy={noop}
        exporting={null}
      />

      {/* Shapes: the kind that needs no network and no signed-in workspace. */}
      <Library open={library} onLayers={noop} onClose={noop} chosen="shapes" onChoose={noop} actions={{ addShape: noop, addText: noop, addPicture: noop, upload: noop, filePictures: () => [], useColour: noop, placeDrawing: noop, placeLayers: noop, placeTemplate: noop, clipSelection: () => null, notice: noop }} />

      {union && state !== 'points' && tool === 'select' ? (
        <QuickBar
          nodes={nodes}
          place={{ box: union, board: boardBox }}
          dragging={false}
          insets={{ left: library ? ASSETS_WIDTH + 16 : 0, top: headerHeight, right, bottom: DOCK + (strip ? STRIP : 0) }}
          actions={actions}
          fonts={[]}
          combinable
          more={panel === 'format'}
          onMore={noop}
          onDuplicate={noop}
        />
      ) : null}

      {panel === 'animate' ? (
        <div className="pointer-events-none absolute inset-x-[8px] bottom-[8px] z-30">
          <AnimateBar
      target={{
              page: { id: doc.board, name: 'Post', animation: null, duration: 5, length: 5, moves: true },
              // `pick` stages the bar with a layer picked: its moves, under the three tabs.
              layer: params.get('pick') ? { id: pick, ids: [pick], name: 'Title', type: 'text', animation: { ...defaultLayerAnimation('rise'), ease: 'smooth' }, inherited: null, timing: null } : null,
            } as AnimationTarget}
            recipe={null}
            choices={null}
            picture={{ kind: 'text' }}
            playback="idle"
            timing={false}
            loop={false}
            hint
            drawingPath={false}
            strip={null}
            selected={[]}
            playhead={() => null}
            onPlay={noop}
            onPause={noop}
            onRecipe={noop}
            onPlayPage={noop}
            onRemove={noop}
            onEdit={noop}
            onTry={noop}
            onWholePage={noop}
            onClose={noop}
            onTiming={noop}
            onLoop={noop}
            onDrawPath={noop}
            onSelect={noop}
            onWindow={noop}
            onDelay={noop}
            onResizeMotion={noop}
            onLength={noop}
            onSeek={noop}
            onHeight={setHeaderHeight}
          />
        </div>
      ) : null}

      {panel === 'format' ? (
        <SidePanel
          onClose={noop}
          tool={tool}
          toolLabel="Draw"
          rail={null}
          drawWith={DRAW_WITH}
          nodes={nodes}
          anchor={null}
          units="px"
          actions={actions}
          fonts={[]}
          page={{ artboard: board, artboards: [{ id: doc.board, name: 'Post', current: true }], units: 'px', onUpdate: actions.update, onFocus: noop, onAdd: noop }}
        />
      ) : null}
    </div>
  );
}
